import { useMemo } from "react";
import { sectionRegistry, type SectionType } from "@sections/registry";

interface LayoutSwitcherProps {
  sectionType: string;
  currentValue: string | number;
  onChange: (value: string | number) => void;
}

/** Human-readable labels for layout enum values. */
const LAYOUT_LABELS: Record<string, string> = {
  // Hero
  centered: "Centered",
  left: "Left-aligned",
  split: "Split",
  image: "Full Image",
  banner: "Banner",
  // TextBlock
  prose: "Rich Text",
  "heading-text": "Heading + Text",
  "two-column": "Two Column",
  "pull-quote": "Pull Quote",
  divider: "Divider",
  // PricingTable
  panels: "Cards",
  "rate-table": "Rate Table",
  // Testimonials
  single: "Single Quote",
  grid: "Grid",
  carousel: "Carousel",
  // FAQ
  accordion: "Accordion",
  // LogoStrip
  row: "Static",
  marquee: "Marquee",
  // CTABanner
  band: "Color Band",
  boxed: "Boxed Card",
  // ProcessSteps
  steps: "Steps",
  timeline: "Timeline",
  // ContactCard / TeamGrid / BlogGrid
  list: "List",
  featured: "Featured",
};

type LayoutVariant =
  | { kind: "layout"; values: string[] }
  | { kind: "columns"; values: number[] };

/**
 * Unwrap Zod wrapper types (ZodDefault, ZodCatch, ZodOptional, ZodNullable,
 * ZodPipe/.transform) to reach the inner schema. Uses Zod 4 public APIs
 * (.unwrap()) with _def fallbacks for edge cases.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapSchema(schema: any): any {
  let current = schema;
  for (let i = 0; i < 10; i++) {
    if (typeof current?.unwrap === "function") {
      current = current.unwrap();
    } else if (current?._def?.in) {
      // ZodPipe (.transform() in Zod 4) — _def.in holds the input schema
      current = current._def.in;
    } else {
      break;
    }
  }
  return current;
}

/**
 * Introspect a section's Zod schema to find layout variants.
 * Returns null for single-variant sections.
 * Wrapped in try/catch so introspection failures never crash the inspector.
 */
function getLayoutVariants(sectionType: string): LayoutVariant | null {
  try {
    const mod = sectionRegistry[sectionType as SectionType];
    if (!mod?.schema) return null;

    // ZodObject exposes .shape as a public property in Zod 4
    const obj = unwrapSchema(mod.schema);
    const shape =
      obj?.shape && typeof obj.shape === "object" ? obj.shape : null;
    if (!shape) return null;

    // Pattern 1: layout enum prop
    if (shape.layout) {
      const values = extractEnumValues(shape.layout);
      if (values && values.length > 1) {
        return { kind: "layout", values };
      }
    }

    // Pattern 2: columns numeric prop (only when no layout prop)
    if (!shape.layout && shape.columns) {
      const values = extractUnionLiterals(shape.columns);
      if (values && values.length > 1) {
        return { kind: "columns", values };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Extract string values from a Zod enum field, unwrapping wrappers first.
 * Uses Zod 4 public API: ZodEnum exposes .options as string[].
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractEnumValues(zodField: any): string[] | null {
  try {
    const inner = unwrapSchema(zodField);
    // Zod 4: ZodEnum.options is a string[] (public API)
    if (
      Array.isArray(inner?.options) &&
      inner.options.every((v: unknown) => typeof v === "string")
    ) {
      return inner.options as string[];
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract numeric literal values from a Zod union of literals,
 * unwrapping wrappers first.
 * Uses Zod 4 public APIs: ZodUnion.options and ZodLiteral.value.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractUnionLiterals(zodField: any): number[] | null {
  try {
    const inner = unwrapSchema(zodField);
    // Zod 4: ZodUnion.options is ZodType[] (public API)
    if (Array.isArray(inner?.options)) {
      const values: number[] = [];
      for (const opt of inner.options) {
        try {
          // Zod 4: ZodLiteral.value is a public getter
          if (typeof opt?.value === "number") {
            values.push(opt.value);
          }
        } catch {
          /* skip unreadable literals */
        }
      }
      if (values.length > 0) return values;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Layout switcher control for the section inspector.
 * Displays a segmented control when a section has multiple layout variants.
 * Returns null for single-variant sections.
 */
export function LayoutSwitcher({
  sectionType,
  currentValue,
  onChange,
}: LayoutSwitcherProps) {
  const variants = useMemo(() => getLayoutVariants(sectionType), [sectionType]);

  if (!variants) return null;

  const base =
    "border-0 text-[11.5px] font-semibold py-1.5 px-2.5 rounded-[7px] transition-all duration-100 cursor-pointer";
  const active = `${base} bg-tide text-white`;
  const inactive = `${base} bg-transparent text-ink-4 hover:text-ink-3`;

  if (variants.kind === "columns") {
    return (
      <div className="mb-[18px]">
        <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
          Columns
        </label>
        <div
          role="group"
          aria-label="Column count"
          className="flex bg-paper-2 rounded-[9px] p-[3px]"
        >
          {variants.values.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={currentValue === n}
              className={`flex-1 ${currentValue === n ? active : inactive}`}
              onClick={() => onChange(n)}
            >
              {n} Columns
            </button>
          ))}
        </div>
      </div>
    );
  }

  // layout enum
  const options = variants.values;

  return (
    <div className="mb-[18px]">
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        Layout
      </label>
      <div
        role="group"
        aria-label="Layout variant"
        className="flex flex-wrap bg-paper-2 rounded-[9px] p-[3px] gap-[2px]"
      >
        {options.map((val) => (
          <button
            key={val}
            type="button"
            aria-pressed={currentValue === val}
            className={currentValue === val ? active : inactive}
            onClick={() => onChange(val)}
          >
            {LAYOUT_LABELS[val] || val}
          </button>
        ))}
      </div>
    </div>
  );
}
